import { Box, Flex, Skeleton } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import WaveformData, { WaveformDataAudioContextOptions } from 'waveform-data'
import { AudioPlayer } from '~/components/shared/AudioPlayer'
import { AudioPlayerVariant } from '~/components/shared/componentStyles/AudioPlayer.style'
import { SentimentSlider } from '~/pages/CallAnalytics/Transcription/Statistics/SentimentSlider'
import { CallTranscript } from '~/types/CallTranscript'

const AudioPlayerWrapper = styled.div`
  display: inline-block;
  min-height: 116px;
`

const StatsWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: flex-start;
  margin: 0 -0.25rem 0 -0.25rem;
`

const MultiFillSliderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: -28px 2px 0px 2px;
  justify-content: space-between;
`

interface TranscriptAudioViewProps {
  audioUri: string
  callTranscript?: CallTranscript
  requestedTimestamp?: number
  requestedIsPlaying?: boolean
  onAudioPlayerPositionChange?: (position: number) => void
  onAudioPlayStateChange: (isPlaying: boolean) => void
}
const TranscriptAudioView = ({
  audioUri,
  callTranscript,
  requestedTimestamp,
  requestedIsPlaying = false,
  onAudioPlayerPositionChange,
  onAudioPlayStateChange: handleAudioPlayStateChange,
}: TranscriptAudioViewProps) => {
  const [waveFormData, setWaveFormData] = useState<WaveformData>()
  const [isAudioPlayerReady, setIsAudioPlayerReady] = useState(false)
  const [didAudioPlayerHaveError, setDidAudioPlayerHaveError] = useState(false)
  const { data: rawAudioBuffer, isLoading: rawAudioBufferLoading } = useQuery({
    queryKey: [audioUri],
    queryFn: async () => {
      const response = await fetch(audioUri)
      const arrayBuffer = await response.arrayBuffer()
      return arrayBuffer
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const buildWaveFormData = () => {
    if (!rawAudioBuffer) {
      return
    }
    const bufferCopy = new ArrayBuffer(rawAudioBuffer.byteLength)
    new Uint8Array(bufferCopy).set(new Uint8Array(rawAudioBuffer))

    const audioContext = new AudioContext()
    const options: WaveformDataAudioContextOptions = {
      audio_context: audioContext,
      array_buffer: bufferCopy,
      scale: 1024,
    }

    WaveformData.createFromAudio(options, (err, waveform) => {
      //WaveforMData incorrectly states err is never null, but its documentation disagrees
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (err) {
        return
      }

      setWaveFormData(waveform)
    })
  }

  useEffect(() => {
    if (!rawAudioBuffer) {
      return
    }

    buildWaveFormData()
  }, [rawAudioBuffer])

  const handleAudioPlayerPositionChange = (position: number) => {
    onAudioPlayerPositionChange?.(position)
  }

  const handleAudioPlayerReady = () => {
    console.log('audio player - handleAudioPlayerReady')
    setIsAudioPlayerReady(true)
  }

  const handleAudioPlayerLoadError = () => {
    setDidAudioPlayerHaveError(true)
  }

  const readyToShowAudioPlayer =
    !rawAudioBufferLoading && waveFormData && callTranscript

  const showPlayerSkeleton =
    !isAudioPlayerReady && !!audioUri && !didAudioPlayerHaveError

  return (
    <Flex flexDirection="column" flex="1" position="relative">
      {showPlayerSkeleton && (
        // We use an absolute positioned box so we can load the audio player in the background
        // while still showing the skeleton
        <Flex
          sx={{ backgroundColor: 'gray.100' }}
          flexDirection="column"
          __css={{
            position: 'absolute',
            top: '-1rem',
            left: '-1.25rem',
            right: '-1.25rem',
            bottom: 0,
            zIndex: 2,
            padding: '1.25rem',
          }}
        >
          <Skeleton h="140px" />
        </Flex>
      )}
      {readyToShowAudioPlayer ? (
        <>
          <AudioPlayerWrapper>
            <AudioPlayer
              variant={AudioPlayerVariant.Analytics}
              audioUri={audioUri}
              showJumpToAnswerButton={false}
              showInfoButton={false}
              showInlineInfoButton={true}
              requestedTimestamp={requestedTimestamp}
              requestedIsPlaying={requestedIsPlaying}
              onValueChanged={handleAudioPlayerPositionChange}
              onPlayStateChange={handleAudioPlayStateChange}
              onReady={handleAudioPlayerReady}
              onLoadError={handleAudioPlayerLoadError}
            />
          </AudioPlayerWrapper>

          <StatsWrapper>
            <MultiFillSliderWrapper>
              <SentimentSlider
                callTranscript={callTranscript}
                margin="4px 2px 0px 2px"
                participantRole="AGENT"
              />
              <SentimentSlider
                callTranscript={callTranscript}
                margin="4px 2px 0px 2px"
                participantRole="CUSTOMER"
              />
            </MultiFillSliderWrapper>
          </StatsWrapper>
        </>
      ) : (
        // This box is just here to make the audio player take up the final height as it loads
        <Flex sx={{ backgroundColor: 'gray.100' }} flexDirection="column">
          <Box h="148px" />
        </Flex>
      )}
    </Flex>
  )
}

export default TranscriptAudioView
